import { Col, Form, Row } from 'antd';
import { get } from 'lodash';
import React from 'react';
import { Helmet } from 'react-helmet';

import CompanyCommuteSettingConfirmModal from '../../../components/Modal/CompanyCommuteSettingConfirmModal';
import Select from '../../../components/Select';
import SpaceSpinner from '../../../components/SpaceSpinner';
import SubmitCancelButtonGroup from '../../../components/SubmitCancelButtonGroup';
import Text from '../../../components/Text';
import { formatPageTitle } from '../../../utils/common';
import { hasSystemAdminRole } from '../../../utils/roles';
import ApprovalWorkflowSettingsFormItems from './ApprovalWorkflowSettingsFormItems';
import BillingSettingsFormItems from './BillingSettingsFormItems';
import CommuteSettingsToggleRow from './CommuteSettingsToggleRow';
import { REIMBURSEMENT_PURPOSE, TAX_RETURN_PURPOSE } from './company-settings-constants';
import CompanyCommuteSettingsSection from './CompanyCommuteSettingsSection';
import InsuranceManagementSettingsFormItems from './InsuranceManagementSettingsFormItems';
import MiscellaneousSettingsFormItems from './MiscellaneousSettingsFormItems';
import NotificationSettingsFormItems from './NotificationSettingsFormItems';
import ReimbursementSettingsFormItems from './ReimbursementSettingsFormItems';
import ReportsSettingsFormItems from './ReportsSettingsFormItems';
import StripeInvoiceSettingsFormItems from './StripeInvoiceSettingsFormItems';
import TripsAndTripsRecordingSettingsFormItems from './TripsAndTripsRecordingSettingsFormItems';
import useCompanySettingsState from './useCompanySettingsState';
import UserManagementSettingsFormItems from './UserManagementSettingsFormItems';
import VehicleManagementSettingsFormItems from './VehicleManagementSettingsFormItems';

const CompanySettings = props => {
  const { t } = props;

  const [form] = Form.useForm();
  const {
    // Boolean
    isSubmitting,
    isCommuteConfirmModalVisible,
    isCommuteSettingsDirty,

    // General
    authUser,
    currentCompany,
    currentCompanySettings,
    companyCommuteSettings,
    currentCompanySettingValues,
    isCommuteSettingCustom,
    isCommuteSettingSingleLocation,
    isCommuteSettingNotHaveOffice,
    isNoOfficeMaxDeduction,
    isHomeAddressMaxCommuteDeduction,
    commuteDistanceUnit,

    // Handlers
    handleFormSave,
    handleActiveFeatureChange,
    handleSettingValueChange,
    handleCommuteSettingChange,
    openCommuteConfirmModal,
    closeCommuteConfirmModal,
  } = useCompanySettingsState({ t, form });

  const handleSubmitCommute = () => {
    if (companyCommuteSettings?.enabled && isCommuteSettingsDirty) {
      openCommuteConfirmModal();
    } else {
      handleFormSave();
    }
  };

  return (
    <Row gutter={[30, 30]}>
      <Helmet>
        <title>{formatPageTitle('Company Settings')}</title>
      </Helmet>

      <React.Suspense fallback={<SpaceSpinner />}>
        <ApprovalWorkflowSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
          companySettingValues={currentCompanySettingValues}
        />

        <TripsAndTripsRecordingSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          companySettingValues={currentCompanySettingValues}
          onFeatureChange={handleActiveFeatureChange}
          onSettingValueChange={handleSettingValueChange}
        />

        <ReimbursementSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <ReportsSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <UserManagementSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <VehicleManagementSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <InsuranceManagementSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <NotificationSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <MiscellaneousSettingsFormItems
          t={t}
          authUser={authUser}
          disabled={isSubmitting}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
        />

        <BillingSettingsFormItems
          t={t}
          disabled={isSubmitting}
          authUser={authUser}
          companySettings={currentCompanySettings}
          onFeatureChange={handleActiveFeatureChange}
          // activePlan={currentCompany.currentPlan}
        />

        <StripeInvoiceSettingsFormItems
          t={t}
          disabled={isSubmitting}
          authUser={authUser}
          companySettings={currentCompanySettings}
          stripeCustomerId={get(currentCompany, 'stripeCustomerId')}
          companySettingValues={currentCompanySettingValues}
          handleSettingValueChange={handleSettingValueChange}
          onFeatureChange={handleActiveFeatureChange}
        />
      </React.Suspense>

      <Col flex="736px">
        <Text renderAs="p" variant="b" size="sm" style={{ marginBottom: 16 }}>
          {t('Settings')}
        </Text>

        <Form
          form={form}
          labelCol={{ span: 24 }}
          onFinish={handleSubmitCommute}
          initialValues={{
            ...companyCommuteSettings,
            mileagePurpose: currentCompany.mileagePurpose,
            fixedReceiptPaymentMethod: currentCompanySettingValues.fixedReceiptPaymentMethod,
          }}
        >
          <Form.Item label={t('fixedReceiptPaymentMethod')} name="fixedReceiptPaymentMethod">
            <Select
              value={currentCompanySettingValues.fixedReceiptPaymentMethod}
              disabled={isSubmitting}
              onChange={value => handleSettingValueChange({ fixedReceiptPaymentMethod: value })}
              options={[
                {
                  value: 'in-arrears',
                  label: t('inArrears'),
                },
                {
                  value: 'up-front',
                  label: t('upFront'),
                },
              ]}
            />
          </Form.Item>

          <Form.Item label={t('mileagePurpose')} name="mileagePurpose">
            <Select
              value={currentCompany.mileagePurpose}
              disabled={isSubmitting || !hasSystemAdminRole(authUser)}
              onChange={value => handleSettingValueChange({ mileagePurpose: value })}
              options={[
                {
                  value: REIMBURSEMENT_PURPOSE,
                  label: t('Reimbursement'),
                },
                {
                  value: TAX_RETURN_PURPOSE,
                  label: t('taxReturn'),
                },
              ]}
            />
          </Form.Item>

          <CommuteSettingsToggleRow
            t={t}
            isSubmitting={isSubmitting}
            checked={companyCommuteSettings?.enabled}
            onCommuteSettingsChange={handleCommuteSettingChange}
          />

          {companyCommuteSettings?.enabled && (
            <CompanyCommuteSettingsSection
              t={t}
              isSubmitting={isSubmitting}
              companyCommuteSettings={companyCommuteSettings}
              onCommuteSettingsChange={handleCommuteSettingChange}
              isCommuteSettingCustom={isCommuteSettingCustom}
              isCommuteSettingSingleLocation={isCommuteSettingSingleLocation}
              isCommuteSettingNotHaveOffice={isCommuteSettingNotHaveOffice}
              isNoOfficeMaxDeduction={isNoOfficeMaxDeduction}
              isHomeAddressMaxCommuteDeduction={isHomeAddressMaxCommuteDeduction}
              distanceUnit={commuteDistanceUnit}
            />
          )}

          <SubmitCancelButtonGroup
            isForm
            hideCancel
            loading={isSubmitting}
            submitText={t('Save')}
          />
        </Form>
      </Col>

      <CompanyCommuteSettingConfirmModal
        t={t}
        visible={isCommuteConfirmModalVisible}
        onCancel={closeCommuteConfirmModal}
        commuteSettingLevel={companyCommuteSettings?.level}
        onConfirm={handleFormSave}
      />
    </Row>
  );
};

export default CompanySettings;
